import {
  loadStripe,
  PaymentRequest,
  PaymentRequestWallet,
  SetupIntentResult,
  Stripe,
  StripeElements,
} from '@stripe/stripe-js';
import {PlanInfo} from '../utils/stripePlans';
import {CardElement} from '@stripe/react-stripe-js';
import Environment from '../utils/Environment';
import * as Sentry from '@sentry/react';
import Analytics from '../utils/Analytics';
import {getUserCountry} from '../utils/locale';

export const createStripeClinicInstance = async (): Promise<Stripe | null | undefined> => {
  try {
    // if running locally, and you don't see this, run the link command
    const key = Environment.getVar('REACT_APP_CLINIC_STRIPE_KEY');
    if (!key) {
      throw new Error('Stripe key must be defined');
    }
    return await loadStripe(key);
  } catch (error: any) {
    Analytics.track('Stripe initialization error', {error});
    Sentry.captureException(new Error(error));
  }
};

/**
 * Stripe instance for the GLP-1 paywall. Backend `/api/stripe-glp1-setup`
 * creates the SetupIntent on a specific Stripe account; if that's not
 * the same account the clinic key belongs to, confirmCardSetup throws
 * "No such setupintent: 'seti_…'" because intent IDs are scoped per
 * account.
 *
 * Set `REACT_APP_GLP1_STRIPE_KEY` to the publishable key matching the
 * backend's GLP-1 Stripe account. Falls back to the clinic key for
 * environments that haven't been configured yet, but expect the
 * mismatch error there.
 */
export const createStripeGlp1Instance = async (): Promise<Stripe | null | undefined> => {
  try {
    const key = Environment.getVar('REACT_APP_GLP1_STRIPE_KEY') ?? Environment.getVar('REACT_APP_CLINIC_STRIPE_KEY');
    if (!key) {
      throw new Error('Stripe key must be defined (REACT_APP_GLP1_STRIPE_KEY or REACT_APP_CLINIC_STRIPE_KEY)');
    }
    return await loadStripe(key);
  } catch (error: any) {
    Analytics.track('GLP-1 Stripe initialization error', {error});
    Sentry.captureException(new Error(error));
  }
};

export const createPaymentRequest = async (
  stripeInstance: Stripe,
  plan: PlanInfo,
  disableWallets: PaymentRequestWallet[] = [],
): Promise<PaymentRequest | undefined> => {
  const paymentRequest = stripeInstance.paymentRequest({
    country: 'US', // country code of merchant
    currency: plan.currency,
    total: {label: plan.frequency, amount: plan.purchasePrice},
    requestPayerEmail: true,
    requestPayerName: true,
    disableWallets,
  });

  const canUseAppleOrGooglePay = await paymentRequest.canMakePayment();
  if (canUseAppleOrGooglePay) {
    const {applePay, googlePay} = canUseAppleOrGooglePay;
    Analytics.track('Platform Pay Options Shown', {applePay, googlePay, ...plan});
    return paymentRequest;
  } else {
    Analytics.track('Platform Pay Options Shown', {applePay: false, googlePay: false, ...plan});
    return undefined;
  }
};

export const validateCreditCard = async (
  clientSecret: string | undefined,
  stripe: Stripe | null,
  elements: StripeElements | null,
): Promise<{
  error: string | null;
  result: SetupIntentResult | null;
}> => {
  const {countryCode: supportedCountry, countryByTimezone} = getUserCountry();

  if (clientSecret === undefined || stripe === null || elements === null) {
    Sentry.captureMessage(`Card Validation Error: Client Secret ${clientSecret};`);
    Analytics.track('Credit Card Validation Failure', {
      clientSecret,
      error: 'A checkout element is null',
      supportedCountry,
      countryByTimezone,
    });

    return {error: 'Please refresh the page and try again', result: null}; // TODO: with i18n make this a key, not text
  }

  const card = elements.getElement(CardElement);
  if (card === null) {
    Sentry.captureMessage('Card Validation Error: Card is null');
    Analytics.track('Credit Card Validation Failure', {
      card: 'null',
      clientSecret,
      supportedCountry,
      countryByTimezone,
    });
    return {error: 'Please enter your card details', result: null}; // TODO: with i18n make this a key, not text
  }

  const result = await stripe.confirmCardSetup(clientSecret, {
    payment_method: {
      card,
    },
  });

  if (result.error) {
    // error via Stripe
    // This point will be reached if there is an immediate error when
    // confirming the payment - for example, payment details incomplete or card declined
    const {code, decline_code, message, request_log_url, type} = result.error;

    Analytics.track('Credit Card Validation Failure', {
      fromStripe: true,
      code,
      decline_code,
      message,
      request_log_url,
      type,
      supportedCountry,
      countryByTimezone,
    });

    return {
      result,
      error: result.error.message ? result.error.message : 'Could not complete checkout, please try again.',
    };
  }
  Analytics.track('Credit Card Validation Success', {supportedCountry, countryByTimezone});
  return {error: null, result};
};

export const confirmCardPayment = async (
  clientSecret: string | undefined,
  stripe: Stripe | null,
  elements: StripeElements | null,
): Promise<{
  error: string | null;
  paymentIntent: any | null;
}> => {
  const {countryCode: supportedCountry, countryByTimezone} = getUserCountry();

  if (clientSecret === undefined || stripe === null || elements === null) {
    Sentry.captureMessage(`Card Payment Error: Client Secret ${clientSecret};`);
    Analytics.track('Credit Card Payment Failure', {
      clientSecret,
      error: 'A checkout element is null',
      supportedCountry,
      countryByTimezone,
    });

    return {error: 'Please refresh the page and try again', paymentIntent: null};
  }

  const card = elements.getElement(CardElement);
  if (card === null) {
    Sentry.captureMessage('Card Payment Error: Card is null');
    Analytics.track('Credit Card Payment Failure', {
      card: 'null',
      clientSecret,
      supportedCountry,
      countryByTimezone,
    });
    return {error: 'Please enter your card details', paymentIntent: null};
  }

  const result = await stripe.confirmCardPayment(clientSecret, {
    payment_method: {
      card,
    },
  });

  if (result.error) {
    const {code, decline_code, message, request_log_url, type} = result.error;

    Analytics.track('Credit Card Payment Failure', {
      fromStripe: true,
      code,
      decline_code,
      message,
      request_log_url,
      type,
      supportedCountry,
      countryByTimezone,
    });

    return {
      paymentIntent: result.paymentIntent,
      error: result.error.message ? result.error.message : 'Could not complete payment, please try again.',
    };
  }

  Analytics.track('Credit Card Payment Success', {supportedCountry, countryByTimezone});
  return {error: null, paymentIntent: result.paymentIntent};
};
